import type { JSONSchema7Definition } from 'json-schema';
import { DYNAMIC_OPTIONS_DSL_PATTERN } from './schemaUtils';
import type { OptionsSchema } from '../types';

const dynamicOptionsDSLType = {
    type: 'string',
    pattern: DYNAMIC_OPTIONS_DSL_PATTERN,
} as const;

/**
 * Helper function to extend an options schema with dynamic options DSL
 * @param optionsSchema a visualization's options schema
 * @returns an updated options schema that additionally allows strings starting with > for each option
 */
export const enhanceOptionsSchemaWithDynamicOptions = (
    optionsSchema: OptionsSchema | JSONSchema7Definition
): OptionsSchema => {
    const enhancedOptionsSchema: OptionsSchema = {};
    Object.keys(optionsSchema).forEach((key): void => {
        const {
            // The next a few are schema annotations, we should leave them as is.
            // Note: in reality we don't use `readOnly`, `writeOnly` or `examples`, but they are listed here for completeness.
            // Ref: https://datatracker.ietf.org/doc/html/draft-handrews-json-schema-validation-01
            title,
            description,
            readOnly,
            writeOnly,
            examples,
            default: defaultValue,
            // The next a few need special care
            type,
            properties,
            patternProperties,
            // The rest should be handled together
            ...remainingSchemaEntry
        } = optionsSchema[key];

        const annotations = {
            ...(title !== undefined && { title }),
            ...(description !== undefined && { description }),
            ...(readOnly !== undefined && { readOnly }),
            ...(writeOnly !== undefined && { writeOnly }),
            ...(examples !== undefined && { examples }),
            ...(defaultValue !== undefined && { default: defaultValue }),
        };

        if (type === 'object') {
            enhancedOptionsSchema[key] = {
                ...remainingSchemaEntry,
                ...annotations,
                type,
                ...(properties && {
                    properties: enhanceOptionsSchemaWithDynamicOptions(properties),
                }),
            };
            if (patternProperties) {
                let enhancedPatternProperties = {};
                Object.keys(patternProperties).forEach((opt): void => {
                    enhancedPatternProperties = {
                        ...enhancedPatternProperties,
                        ...enhanceOptionsSchemaWithDynamicOptions({
                            [opt]: patternProperties[opt],
                        }),
                    };
                });
                enhancedOptionsSchema[key].patternProperties = enhancedPatternProperties;
            }
        } else {
            enhancedOptionsSchema[key] = {
                ...annotations,
                anyOf: [
                    // we can't assume `type` is always there because there are `enum` and `const`.
                    {
                        ...(type !== undefined && { type }),
                        ...remainingSchemaEntry,
                    },
                    dynamicOptionsDSLType,
                ],
            };
        }
    });
    return enhancedOptionsSchema;
};
